import type { Route, Itinerary, ScheduleItem } from '@/types';

const DEPARTURE_WINDOW_MS = 130 * 60 * 1000; // 130 minutes in milliseconds

/**
 * Determines if a departure should be shown based on time window
 * Shows departures within 0-130 minutes from now
 */
export function shouldShowDeparture(departureTime: number): boolean {
  const now = Date.now();
  const departureMs = departureTime * 1000;
  const diff = departureMs - now;
  return diff > 0 && diff <= DEPARTURE_WINDOW_MS;
}

/**
 * Checks if an itinerary has any departures that should be shown
 */
export function hasShownDepartureInItinerary(itinerary: Itinerary): boolean {
  return itinerary.schedule_items.some((item) =>
    shouldShowDeparture(item.departure_time)
  );
}

/**
 * Checks if a route has any departures that should be shown
 */
export function hasShownDeparture(route: Route): boolean {
  return route.itineraries.some(hasShownDepartureInItinerary);
}

/**
 * Gets the minutes until departure
 */
export function getMinutesUntilDeparture(departureTime: number): number {
  const now = Date.now();
  const departureMs = departureTime * 1000;
  return Math.max(0, Math.floor((departureMs - now) / 60000));
}

/**
 * Filters schedule items to only show valid departures
 */
export function filterValidDepartures(scheduleItems: ScheduleItem[]): ScheduleItem[] {
  return scheduleItems.filter((item) => shouldShowDeparture(item.departure_time));
}

/**
 * Gets the first N valid departures from an itinerary
 */
export function getDisplayDepartures(
  itinerary: Itinerary,
  maxDepartures: number = 3
): ScheduleItem[] {
  return filterValidDepartures(itinerary.schedule_items).slice(0, maxDepartures);
}
